import React, { useEffect, useState, useRef } from 'react'
import { AiOutlineMessage, AiOutlinePlus } from 'react-icons/ai'
import { GrEmoji } from 'react-icons/gr'
import { IoSend } from 'react-icons/io5'
import { FaList } from 'react-icons/fa'
import { Link, useParams, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import io from 'socket.io-client'
import {
  add_friend,
  send_message,
  updateMessage,
  messageClear
} from '../../store/reducers/chatReducer'
import toast from 'react-hot-toast'
import { base_url } from '../../utils/config'

// initialize socket once
const socket = io(base_url)

const Chat = () => {
  const scrollRef = useRef()
  const dispatch = useDispatch()
  const { sellerId } = useParams()
  const location = useLocation()

  const [text, setText] = useState('')          // for input field
  const [receverMessage, setReceverMessage] = useState(null)
  const [activeSeller, setActiveSeller] = useState([])
  const { userInfo } = useSelector(state => state.auth)
  const {
    fd_messages,
    currentFd,
    my_friends,
    successMessage
  } = useSelector(state => state.chat)
  const [show, setShow] = useState(false)       // toggle sidebar
  const [sentProductMsg, setSentProductMsg] = useState(false)

  /**
   * 1️⃣ Always fetch friend list (and if there's a sellerId, also fetch that conversation)
   */
  useEffect(() => {
    const productData = location.state?.product || null
    dispatch(add_friend({
      sellerId: sellerId || "",   // empty string triggers friend-list only branch
      userId:   userInfo.id,
      product:  productData
    }))
  }, [dispatch, sellerId, userInfo.id, location.state])

  /**
   * 2️⃣ Send one-off product message if navigated from Details with state
   */
  useEffect(() => {
    const productData = location.state?.product
    if (productData && !sentProductMsg) {
      dispatch(send_message({
        userId:   userInfo.id,
        text:     JSON.stringify({
          type:     'product',
          name:     productData.name,
          price:    productData.price,
          discount: productData.discount,
          images:   productData.images
        }),
        sellerId,
        name:     userInfo.name
      }))
      setSentProductMsg(true)
    }
  }, [dispatch, location.state, sellerId, userInfo.id, sentProductMsg])

  /**
   * 3️⃣ Socket listeners for incoming messages and active status
   */
  useEffect(() => {
    socket.emit('add_user', userInfo.id, userInfo)
    socket.on('seller_message', msg => setReceverMessage(msg))
    socket.on('activeSeller', sellers => setActiveSeller(sellers))
    return () => {
      socket.off('seller_message')
      socket.off('activeSeller')
    }
  }, [userInfo])

  /**
   * 4️⃣ When a message is successfully sent, notify server
   */
  useEffect(() => {
    if (successMessage) {
      socket.emit('send_customer_message', fd_messages[fd_messages.length - 1])
      dispatch(messageClear())
    }
  }, [successMessage, fd_messages, dispatch])

  /**
   * 5️⃣ Handle incoming seller message in Redux or via toast
   */
  useEffect(() => {
    if (receverMessage) {
      if (sellerId === receverMessage.senderId && userInfo.id === receverMessage.receverId) {
        dispatch(updateMessage(receverMessage))
      } else {
        toast.success(`${receverMessage.senderName} sent a message`)
        dispatch(messageClear())
      }
    }
  }, [receverMessage, sellerId, userInfo.id, dispatch])

  /**
   * 6️⃣ Auto-scroll chat
   */
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [fd_messages])

  const sendText = () => {
    if (!text) return
    dispatch(send_message({
      userId:   userInfo.id,
      text,
      sellerId,
      name:     userInfo.name
    }))
    setText('')
  }

  // helper to parse JSON product messages
  const parseMessage = msg => {
    try { return JSON.parse(msg) }
    catch { return null }
  }

  return (
    <div className='bg-white p-3 rounded-md flex'>
      {/* Sidebar: list of sellers */}
      <div className={`
          w-[230px] absolute transition-all h-full bg-white
          ${show ? 'left-0' : '-left-[300px]'}
          md:relative md:left-0
        `}>
        <div className='flex items-center gap-3 p-3 text-slate-600 text-xl'>
          <AiOutlineMessage /> <span>Messages</span>
        </div>
        <div className='overflow-y-auto h-[calc(100vh-150px)]'>
          {my_friends.map((f, i) => (
            <Link
              to={`/dashboard/chat/${f.fdId}`}
              key={i}
              className='flex gap-2 items-center p-2 hover:bg-gray-100'
            >
              <div className='relative'>
                <img src="http://localhost:3000/images/user.png" alt="" className='w-8 h-8 rounded-full' />
                {activeSeller.some(a => a.sellerId === f.fdId) && (
                  <span className='w-2 h-2 bg-green-500 rounded-full absolute bottom-0 right-0' />
                )}
              </div>
              <span>{f.name}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div className='flex-1 ml-[230px] md:ml-0'>
        {currentFd ? (
          <>
            {/* Header */}
            <div className='flex justify-between items-center p-3 border-b'>
              <div className='flex items-center gap-2 text-slate-600'>
                <img src="http://localhost:3000/images/user.png" className='w-8 h-8 rounded-full' alt='' />
                <span className='font-semibold'>{currentFd.name}</span>
              </div>
              <button
                onClick={() => setShow(prev => !prev)}
                className='hidden md:block bg-sky-600 text-white px-4 py-1 rounded'
              >Chat Sellers</button>
            </div>

            {/* Messages list */}
            <div className='p-3 bg-slate-100 h-[calc(100vh-240px)] overflow-y-auto flex flex-col gap-3'>
              {fd_messages.map((m, idx) => {
                const isMyMsg = m.senderId === userInfo.id
                const parsed = parseMessage(m.message)
                const isProduct = parsed?.type === 'product'
                const containerClass = isMyMsg ? 'justify-end' : 'justify-start'

                return (
                  <div key={idx} ref={scrollRef} className={`flex ${containerClass} items-center gap-2`}> 
                    {!isMyMsg && (
                      <img src="http://localhost:3000/images/user.png" className='w-6 h-6 rounded-full' alt='' />
                    )}

                    <div className={`p-2 rounded-md text-white 
                      ${isProduct ? 'bg-purple-600' : isMyMsg ? 'bg-cyan-600' : 'bg-purple-600'}`}> 
                      {isProduct ? (
                        <>
                          <img src={parsed.images[0]} alt={parsed.name} className='w-16 h-16 object-cover mb-1' />
                          <div className='font-bold'>{parsed.name}</div>
                          <div>Price: {parsed.price}</div>
                          {parsed.discount && <div>Discount: {parsed.discount}%</div>}
                        </>
                      ) : (
                        <span>{m.message}</span>
                      )}
                    </div>

                    {isMyMsg && (
                      <img src="http://localhost:3000/images/user.png" className='w-6 h-6 rounded-full' alt='' />
                    )}
                  </div>
                )
              })}
            </div>

            {/* Input */}
            <div className='flex items-center gap-2 p-3 border-t'>
              <div className='p-2 border rounded-full'>
                <AiOutlinePlus className='cursor-pointer' />
              </div>
              <div className='flex-1 relative'>
                <input
                  value={text}
                  onChange={e => setText(e.target.value)}
                  className='w-full p-2 border rounded-full pr-10'
                  type='text'
                  placeholder='Type a message'
                />
                <GrEmoji className='absolute right-3 top-3 text-xl' />
              </div>
              <button onClick={sendText} className='p-2 rounded-full'><IoSend className='text-xl cursor-pointer' /></button>
            </div>
          </>
        ) : (
          <div
            onClick={() => setShow(true)}
            className='h-[400px] flex items-center justify-center text-gray-500'
          >
            <span>Select a seller</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default Chat